import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// IMPORTY FIREBASE
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';

// DODANO: Funkcje do zapisu i przeszukiwania bazy danych
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const navigate = useNavigate();

  // STANY NA DANE FORMULARZA I BŁĘDY
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // --- ZMIANA: Naprawiony useEffect ---
  // Teraz czyści formularz tylko wtedy, gdy otwieramy modal,
  // i nie blokuje ręcznej zmiany trybu!
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError('');
      setUsername('');
      setEmail('');
      setPassword('');
    }
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  // LOGIKA LOGOWANIA / REJESTRACJI
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'login') {
        // Logowanie
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // SPRAWDZANIE UNIKALNOŚCI NICKU
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('username', '==', username));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          setError(
            'This username is already taken. Please choose another one.',
          );
          setIsLoading(false);
          return;
        }

        // 1. Rejestracja w systemie uwierzytelniania
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        const user = userCredential.user;

        // 2. Aktualizacja profilu wewnętrznego (Auth)
        await updateProfile(user, {
          displayName: username,
        });

        // 3. ZAPIS DO GŁÓWNEJ BAZY DANYCH (FIRESTORE)
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          username: username,
          email: email,
          photoURL: '',
          createdAt: serverTimestamp(),
          followersCount: 0,
          following: [],
        });
      }

      onClose();
      navigate('/home');
    } catch (err) {
      // console.error(err);
      if (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password'
      ) {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="animate-fadeIn fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-[480px] flex-col items-center rounded-[32px] bg-white p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-5 top-4 text-3xl font-bold leading-none text-gray-400 hover:text-gray-800"
        >
          &times;
        </button>

        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-[#E60023]/10 text-[#E60023]">
          <img src="/icons/logo.svg" className="w-14" alt="Boardly Logo" />
        </div>

        <h2 className="mb-2 font-Outfit text-3xl font-bold text-gray-800">
          {mode === 'login' ? 'Welcome back' : 'Welcome to Boardly'}
        </h2>
        <p className="mb-6 text-center font-Outfit text-gray-500">
          Find new ideas to try
        </p>

        <div
          className={`w-full overflow-hidden transition-all duration-300 ease-in-out ${
            error ? 'mb-4 max-h-24 opacity-100' : 'mb-0 max-h-0 opacity-0'
          }`}
        >
          <div className="rounded-xl bg-red-100 p-3 text-center text-sm font-semibold text-red-600">
            {error}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3">
          {mode === 'signup' && (
            <div className="flex flex-col gap-1">
              <label className="ml-2 text-sm font-semibold text-gray-600">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length > 14) {
                    setError('Username can be up to 14 characters long.');
                  } else {
                    setUsername(val);
                    // Czyścimy błąd o limicie, jeśli użytkownik coś usunie
                    if (error === 'Username can be up to 14 characters long.') {
                      setError('');
                    }
                  }
                }}
                placeholder="Username"
                // USUNIĘTO: maxLength={14}
                className="w-full rounded-[16px] border-2 border-[#E1E1E1] px-4 py-3 font-Outfit text-lg outline-none transition-colors focus:border-[#620a85]"
                required={mode === 'signup'}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="ml-2 text-sm font-semibold text-gray-600">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="Email"
              className="w-full rounded-[16px] border-2 border-[#E1E1E1] px-4 py-3 font-Outfit text-lg outline-none transition-colors focus:border-[#620a85]"
              required
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="ml-2 text-sm font-semibold text-gray-600">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-[16px] border-2 border-[#E1E1E1] px-4 py-3 font-Outfit text-lg outline-none transition-colors focus:border-[#620a85]"
              required
              minLength={6}
            />
          </div>

          {mode === 'login' && (
            <a
              href="#"
              className="ml-2 w-fit text-sm font-bold text-gray-900 hover:underline"
            >
              Forgot your password?
            </a>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="mt-4 w-full rounded-[24px] bg-[#620a85] py-3 text-lg font-bold text-white transition-colors hover:bg-[#50086c] disabled:opacity-50"
          >
            {isLoading
              ? 'Loading...'
              : mode === 'login'
                ? 'Log in'
                : 'Continue'}
          </button>
        </form>

        <div className="mt-6 text-sm">
          {mode === 'login' ? (
            <>
              Not on Boardly yet?{' '}
              <button
                type="button"
                onClick={() => setMode('signup')}
                className="font-bold text-gray-900 hover:underline"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already a member?{' '}
              <button
                type="button"
                onClick={() => setMode('login')}
                className="font-bold text-gray-900 hover:underline"
              >
                Log in
              </button>
            </>
          )}
        </div>

        <p className="mt-8 max-w-xs text-center text-xs text-gray-400">
          By continuing, you agree to Boardly's Terms of Service and confirm
          that you have read Boardly's Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
